import taxData from "../data/tax-duties-data.json";
import { exchangeCurrency } from "@/utils/currency";
import {
  getApplicableTaxNumberTypeForLocation,
  isPostalToIossSchemeLocation,
} from "./shipment-functions";
import { getApprovedTaxNumberByTypeForUser } from "@/data/db/ecommerce/tax-numbers";

const THRESHOLD_BUFFER = 0.02; // 2%
export const INSURANCE_MULTIPLIER = 0.024;
export const COURIER_HANDLING_FEE = 20;
export const POSTAL_EU_CUSTOMS_HANDLING_FEE = 1;
export const FOB_COUNTRIES = ["AU", "US"];

function getTaxThresholdSGD(country) {
  const currency = taxData[country]?.currency;
  if (currency) {
    return exchangeCurrency(
      taxData[country].TAX_THRESHOLD_REC_CCY * (1 - THRESHOLD_BUFFER),
      currency,
      "SGD"
    );
  }
  return 0;
}

function getDutyThresholdSGD(country) {
  const currency = taxData[country]?.currency;
  if (currency) {
    return exchangeCurrency(
      taxData[country].DUTY_THRESHOLD_REC_CCY * (1 - THRESHOLD_BUFFER),
      currency,
      "SGD"
    );
  }
  return 0;
}

export const getInsuranceCost = (customsValue, isInsured) => {
  return isInsured ? customsValue * INSURANCE_MULTIPLIER : 0;
};

const getDutiesPayable = (dutyRate, country, customsValue) => {
  let dutiesPayable = 0;
  if (customsValue < getDutyThresholdSGD(country)) return 0;

  dutiesPayable = customsValue * dutyRate || 0;
  return dutiesPayable;
};

const getHighestDutyRate = (packages, country) => {
  let highestDutyRate = 0;
  for (const parcel of packages) {
    for (const item of parcel.items) {
      let dutyRate = taxData[country].DUTY_RATES[item.category];
      highestDutyRate = dutyRate > highestDutyRate ? dutyRate : highestDutyRate;
    }
  }
  return highestDutyRate;
};

const getTaxesPayable = (country, customsValue) => {
  let taxesPayable = 0;
  if (customsValue < getTaxThresholdSGD(country)) return 0;
  const TAX_RATE = taxData?.[country]?.TAX_RATES || 0;
  taxesPayable = customsValue * TAX_RATE;
  return taxesPayable;
};

export const getDutiesAndTaxesPayable = async ({
  packages,
  country,
  postalCode = null,
  isInsured,
  freightCost,
  shippingMethod,
  userID = null,
}) => {
  if (!country)
    return {
      taxes: 0,
      duties: 0,
      dutyHandlingFee: 0,
      originalPayableAmounts: {
        taxes: 0,
        duties: 0,
        dutyHandlingFee: 0,
      },
    };

  let shouldIgnoreDutiesAndTaxes = false;
  const taxScheme = getApplicableTaxNumberTypeForLocation(country, postalCode);
  if (!!taxScheme && !!userID) {
    shouldIgnoreDutiesAndTaxes = !!(await getApprovedTaxNumberByTypeForUser(
      userID.toString(),
      { type: taxScheme }
    ));
  }

  let customsValue = getTotalDeclaredValue(packages);
  if (!FOB_COUNTRIES.includes(country)) {
    customsValue += getInsuranceCost(customsValue, isInsured);
    customsValue += freightCost;
  }

  const dutiesPayable = getDutiesPayable(
    getHighestDutyRate(packages, country),
    country,
    customsValue
  );
  const taxesPayable = getTaxesPayable(country, customsValue);
  const handlingFeePayable =
    (isPostalToIossSchemeLocation(shippingMethod, country, postalCode) &&
      POSTAL_EU_CUSTOMS_HANDLING_FEE) ||
    ((taxesPayable > 0 || dutiesPayable > 0) && COURIER_HANDLING_FEE) ||
    0;
  return {
    taxes: !shouldIgnoreDutiesAndTaxes ? taxesPayable : 0,
    duties: !shouldIgnoreDutiesAndTaxes ? dutiesPayable : 0,
    dutyHandlingFee: !shouldIgnoreDutiesAndTaxes ? handlingFeePayable : 0,
    originalPayableAmounts: {
      taxes: taxesPayable,
      duties: dutiesPayable,
      dutyHandlingFee: handlingFeePayable,
    },
  };
};

export const getTotalDutiesAndTaxesPayable = ({
  duties = 0,
  taxes = 0,
  dutyHandlingFee = 0,
}) => {
  return duties + taxes + dutyHandlingFee;
};

export const getDutyHandlingFee = ({ taxes, duties }) => {
  const hasDutiesAndTaxes = taxes > 0 || duties > 0;
  return hasDutiesAndTaxes ? COURIER_HANDLING_FEE : 0;
};

export const getTotalDeclaredValue = (packages) => {
  const totalItemsValue = packages.reduce((acc, currentPackage) => {
    return (
      acc +
      currentPackage.items.reduce((itemAcc, currentItem) => {
        return itemAcc + parseFloat(currentItem.totalValue);
      }, 0)
    );
  }, 0);
  return totalItemsValue || 0;
};
